import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Lock, ShieldCheck } from "lucide-react";

// Feb 2026 — mandatory first-login password reset for staff users.
// ProtectedRoute bounces any authenticated user with `must_reset_password:true`
// here and blocks every other route until the flag clears.

export default function SetPasswordPage() {
  const { principal, logout } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== confirm) { setErr("Passwords don't match."); return; }
    if (pw === "Admin@123" || pw === "Interazzo@123" || pw === "Client@123") {
      setErr("Please choose a password different from the default.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/user-set-password", { new_password: pw });
      toast.success("Password updated. Please sign in with your new password.");
      // Force a fresh session so must_reset_password is cleared everywhere.
      await logout?.();
      window.location.href = "/login";
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] p-4">
      <form onSubmit={submit} className="iz-card p-8 max-w-sm w-full" data-testid="staff-set-pw-form">
        <div className="flex items-start gap-3 mb-6">
          <div className="w-11 h-11 rounded-full bg-[#d4af37]/10 border border-[#d4af37] flex items-center justify-center flex-shrink-0">
            <Lock className="w-4 h-4 text-[#d4af37]"/>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Mandatory · First Login</p>
            <h1 className="font-display text-2xl text-white mt-0.5">Set your password</h1>
            <p className="text-[11px] text-gray-500 font-mono mt-0.5">{principal?.email}</p>
          </div>
        </div>

        <div className="border border-amber-500/30 bg-amber-500/5 rounded-md px-3 py-2 mb-5 flex items-start gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0"/>
          <p className="text-[11px] text-amber-300 leading-relaxed">
            You must set a new password before accessing any Interazzo workspace.
            Minimum 8 characters, distinct from the default issued by Admin.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">New password</label>
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                   className="iz-input" data-testid="new-pw" autoFocus required minLength={8}/>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Confirm password</label>
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   className="iz-input" data-testid="confirm-pw" required minLength={8}/>
          </div>
          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2" data-testid="set-pw-error">{err}</div>}
          <button type="submit" disabled={saving} className="iz-btn-primary w-full" data-testid="set-pw-submit">
            {saving ? "Saving…" : "Set password & continue"}
          </button>
        </div>

        <button type="button" onClick={async () => { await logout?.(); window.location.href = "/login"; }}
                 className="text-[10px] font-mono uppercase tracking-widest text-gray-500 hover:text-[#d4af37] mt-6 w-full text-center"
                 data-testid="set-pw-signout">
          Sign out
        </button>
      </form>
    </div>
  );
}
